import { createApp, h } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNuxtApp, useRuntimeConfig } from '#app';

// Import all components
import Spotlights from '~/components/shortcodes/Spotlights.vue';
import VueLink from '~/components/shortcodes/VueLink.vue';
import IntroDialog from '~/components/shortcodes/IntroDialog.vue';
import PortfolioClip from '~/components/shortcodes/PortfolioClip.vue';
import Banner from '~/components/shortcodes/Banner.vue';
import ServiceFeatures from '~/components/shortcodes/ServiceFeatures.vue';
import ServiceContent from '~/components/shortcodes/ServiceContent.vue';
import SpotlightFade from '~/components/shortcodes/SpotlightFade.vue';
import CircleDazzling from '~/components/shortcodes/CircleDazzling.vue';
import ExecutiveTalk from '~/components/shortcodes/ExecutiveTalk.vue';
import Portfolio from '~/components/shortcodes/Portfolio.vue';
import Customer from '~/components/shortcodes/Customer.vue';
import Lightbox from '~/components/shortcodes/Lightbox.vue';
import AboutClip from '~/components/shortcodes/AboutClip.vue';
import Milestone from '~/components/shortcodes/Milestone.vue';
import NavbarAbout from '~/components/shortcodes/NavbarAbout.vue';
import OurTeam from '~/components/shortcodes/OurTeam.vue';
import GallerySI from '~/components/shortcodes/GallerySI.vue';

import ServiceList from '~/components/shortcodes/ServiceList.vue';


export const useShortcodeParser = () => {
  const { locale } = useI18n();
  const config = useRuntimeConfig();
  const { baseApi } = useApiBase();
  const appApi = config.public.appApi;
  const { $router } = useNuxtApp();

  // Cache storage for parsed shortcodes
  const cache = new Map();

  const shortcodeRegistry = {
    'lightbox': { 
      component: Lightbox,
    },
    'vuelink': { 
      component: VueLink,
    },
    'portfolio-clip': { 
      component: PortfolioClip,
      fetchData: async (attrs) => {
        const result = { 
          ...attrs
        };
        return result;
      },
    },
    'banner': { 
      component: Banner,
    },
    'service-content': { 
      component: ServiceContent,
    },
    'spotlight-fade': { 
      component: SpotlightFade,
    },
    'circle-dazzling': { 
      component: CircleDazzling,
    },
    'about-clip': { 
      component: AboutClip,
    },
    'executive-talk': { 
      component: ExecutiveTalk,
    },
    'service-lists': { 
      component: ServiceList,
      fetchData: async (attrs) => {
        
        const category = attrs.category;
        const cacheKey = `service-lists-${locale.value}-${category}`;

        if (cache.has(cacheKey)) {
          return cache.get(cacheKey);
        }
        
        const data = await $fetch(`${baseApi}/${locale.value}/posts/one-stop-service`);
        const result = {
          posts: data?.data || [],
          ...attrs
        };

        // Cache the result
        cache.set(cacheKey, result);
        return result;
      },
    },
    'service-features': {
      component: ServiceFeatures,
      fetchData: async (attrs) => {
        
        const category = attrs.category;
        const cacheKey = `service-features-${locale.value}-${category}`;

        if (cache.has(cacheKey)) {
          return cache.get(cacheKey);
        }
        
        const data = await $fetch(`${baseApi}/${locale.value}/posts/service-features/${category}`);
        const result = {
          posts: data?.data || [],
          ...attrs
        };

        // Cache the result
        cache.set(cacheKey, result);
        return result;
      },
    },
    'customer': {
      component: Customer,
      fetchData: async (attrs) => {

        const data = await $fetch(`${baseApi}/${locale.value}/our-clients/our-customer`);
        const posts = data.data

        if(attrs.category){
          
          // filter by category
          const filteredPosts = posts.filter(post => post.categories.includes(attrs.category));

          const result = { 
            posts: filteredPosts || [],
            ...attrs
          };
          return result;
        }        
    
        return {
          posts: posts || [],
          ...attrs  
        };

      },
    },
    'portfolio': { 
      component: Portfolio,
      fetchData: async (attrs) => {

        const data = await $fetch(`${baseApi}/${locale.value}/our-clients/our-portfolio`);
        const posts = data.data

        if(attrs.category){
          
          // filter by category
          const filteredPosts = posts.filter(post => post.categories.find((cat) => cat.name === attrs.category));
          const result = { 
            posts: filteredPosts || [],
            ...attrs
          };
          return result;
        }        
    
        return {
          posts: posts || [],
          ...attrs  
        };

      },
    },
    'intro-dialog': {
      component: IntroDialog,
      fetchData: async (attrs) => {
        const cacheKey = `intro-dialog-${locale.value}`;
        if (cache.has(cacheKey)) {
          return cache.get(cacheKey);
        }

        const data = await $fetch(`${baseApi}/${locale.value}/intro-dialog`);
        const result = {
          posts: data?.data || [],
          ...attrs
        };
        
        // Cache the result
        cache.set(cacheKey, result);
        return result;
      },
    },
  'milestone': { 
    component: Milestone,
    fetchData: async (attrs) => {
      const cacheKey = `milestone-${locale.value}`;
      if (cache.has(cacheKey)) {
        return cache.get(cacheKey);
      }
      const data = await $fetch(`${appApi}/${locale.value}/posts/mile-stone`);
      const result = {
        posts: data?.data || [],
        ...attrs
      };
      
      // Cache the result
      cache.set(cacheKey, result);
      return result;
    },
  },
    'navbar-about': { 
      component: NavbarAbout,
    },
    'our-team': { 
      component: OurTeam,
      fetchData: async (attrs) => {
        const cacheKey = `our-team-${locale.value}`;
        if (cache.has(cacheKey)) {
          return cache.get(cacheKey);
        }
        const data = await $fetch(`${appApi}/${locale.value}/posts/our-team`);
        const result = {
          posts: data?.data || [],
          ...attrs
        };
        
        // Cache the result
        cache.set(cacheKey, result);
        return result;
      },
    },
    'gallery-si': { 
      component: GallerySI,
      fetchData: async (attrs) => {
        const cacheKey = `gallery-si-${locale.value}`;
        if (cache.has(cacheKey)) {
          return cache.get(cacheKey);
        }
        const data = await $fetch(`${appApi}/${locale.value}/posts/gallery-si`);
        const result = {
          posts: data?.data || [],
          ...attrs
        };
        
        // Cache the result
        cache.set(cacheKey, result);
        return result;
      },
    },
  };

  const parseShortcodes = async (content) => {
    // Regex to match both [shortcode] and [shortcode][/shortcode]
    const regex = /\[([\w-]+)(.*?)\](?:([\s\S]*?)\[\/\1\])?/g;
    let match;
    let parsedContent = content;
  
    while ((match = regex.exec(content)) !== null) {
      const shortcode = match[1];
      const attrString = match[2];
      const innerContent = match[3] || ""; // Content between opening and closing shortcode tags
  
      // Parse attributes
      const attrs = {};
      const attrRegex = /(\w+)="(.*?)"/g;
      let attrMatch;
      while ((attrMatch = attrRegex.exec(attrString)) !== null) {
        attrs[attrMatch[1]] = attrMatch[2];
      }
  
      const entry = shortcodeRegistry[shortcode];
      if (!entry) {
        console.warn(`Shortcode not registered: ${shortcode}`);
        continue;
      }
  
      // Generate a unique ID for the container
      const containerId = `${shortcode}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      parsedContent = parsedContent.replace(
        match[0],
        `<div id="${containerId}" style="display: contents"></div>`
      );
  
      // Render the component. The placeholder div above isn't in the real DOM
      // yet at this point — the caller hasn't even received `parsedContent`
      // back to assign to a reactive ref — so poll briefly instead of
      // assuming a single setTimeout(0) tick is enough for it to appear.
      const mountShortcode = (attemptsLeft = 20) => {
        const container = document.getElementById(containerId);

        if (!container) {
          if (attemptsLeft > 0) {
            setTimeout(() => mountShortcode(attemptsLeft - 1), 50);
          } else {
            console.warn(`Shortcode container never appeared: ${containerId}`);
          }
          return;
        }

        (async () => {
          // Cache data for this shortcode
          const cacheKey = `${shortcode}-${JSON.stringify(attrs)}`;
          let props;
          if (cache.has(cacheKey)) {
            props = cache.get(cacheKey);
          } else {
            props = entry.fetchData ? await entry.fetchData(attrs) : attrs;
            cache.set(cacheKey, props);
          }

          // Include inner content as a prop if supported by the component
          props.innerContent = innerContent;

          const app = createApp(entry.component, props);
          app.use($router);
          app.mount(container);
        })();
      };

      setTimeout(() => mountShortcode(), 0);



    }
  
    return parsedContent;
  };

  return { parseShortcodes };
};